import { useState, useMemo } from 'react';
import { GetTrainingAdminPoliciesOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Plus, AlertTriangle, Clock, CheckCircle2, Shield, FileText, BarChart3, Archive, ChevronUp, ChevronDown, Users, ArrowUpDown } from 'lucide-react';
import { format, parseISO } from 'date-fns';

type Policy = GetTrainingAdminPoliciesOutputType['policies'][0];
type Category = GetTrainingAdminPoliciesOutputType['categories'][0];

interface Props {
  policies: Policy[];
  categories: Category[];
  stats: GetTrainingAdminPoliciesOutputType['stats'] | null;
  onNewPolicy: () => void;
  onEditPolicy: (id: string) => void;
  onViewPolicy: (id: string) => void;
  onRefresh: () => void;
}

const STATUS_COLORS: Record<string, string> = {
  Draft: 'bg-muted text-muted-foreground',
  'Under Review': 'bg-yellow-500/10 text-yellow-700',
  Approved: 'bg-blue-500/10 text-blue-700',
  Published: 'bg-primary/10 text-primary',
  Superseded: 'bg-orange-500/10 text-orange-700',
  Withdrawn: 'bg-destructive/10 text-destructive',
  Archived: 'bg-muted text-muted-foreground',
};

type SortField = 'name' | 'status' | 'version' | 'reviewDate' | 'effectiveDate' | 'acknowledged';
type SortDir = 'asc' | 'desc';

export default function PolicyListView({ policies, categories, stats, onNewPolicy, onEditPolicy, onViewPolicy, onRefresh }: Props) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const uniqueOwners = useMemo(() => {
    const map = new Map<string, string>();
    policies.forEach(p => { if (p.ownerId && p.ownerName) map.set(p.ownerId, p.ownerName); });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [policies]);

  const filtered = useMemo(() => {
    let list = policies;
    if (!showArchived) list = list.filter(p => p.lifecycleStatus !== 'Archived');
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(p => p.policyName.toLowerCase().includes(s) || (p.policyNumber || '').toLowerCase().includes(s) || (p.summary || '').toLowerCase().includes(s));
    }
    if (statusFilter !== 'all') list = list.filter(p => p.lifecycleStatus === statusFilter);
    if (categoryFilter !== 'all') list = list.filter(p => p.primaryCategoryId === categoryFilter);
    if (deptFilter !== 'all') list = list.filter(p => (p.applicableDepartments || []).includes(deptFilter));
    if (roleFilter !== 'all') list = list.filter(p => (p.applicableRoles || []).includes(roleFilter));
    if (ownerFilter !== 'all') list = list.filter(p => p.ownerId === ownerFilter);

    // Sort
    list = [...list].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name': cmp = a.policyName.localeCompare(b.policyName); break;
        case 'status': cmp = (a.lifecycleStatus || '').localeCompare(b.lifecycleStatus || ''); break;
        case 'version': cmp = (a.version || '').localeCompare(b.version || ''); break;
        case 'reviewDate': cmp = (a.reviewDate || '').localeCompare(b.reviewDate || ''); break;
        case 'effectiveDate': cmp = (a.effectiveDate || '').localeCompare(b.effectiveDate || ''); break;
        case 'acknowledged': cmp = a.acknowledgedCount - b.acknowledgedCount; break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [policies, search, statusFilter, categoryFilter, deptFilter, roleFilter, ownerFilter, showArchived, sortField, sortDir]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  const activeFilterCount = [statusFilter !== 'all', categoryFilter !== 'all', deptFilter !== 'all', roleFilter !== 'all', ownerFilter !== 'all'].filter(Boolean).length;

  return (
    <div>
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard icon={<FileText className="w-4 h-4" />} label="Total" value={stats.total} />
          <StatCard icon={<CheckCircle2 className="w-4 h-4 text-primary" />} label="Published" value={stats.published} />
          <StatCard icon={<Shield className="w-4 h-4" />} label="Draft" value={stats.draft} />
          <StatCard icon={<Clock className="w-4 h-4 text-yellow-600" />} label="Review Due" value={stats.reviewDue} />
          <StatCard icon={<AlertTriangle className="w-4 h-4 text-destructive" />} label="Overdue Review" value={stats.reviewOverdue} />
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-foreground">Company Policies</h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowArchived(!showArchived)}>
            <Archive className="w-4 h-4 mr-1" />{showArchived ? 'Hide Archived' : 'Show Archived'}
          </Button>
          <Button size="sm" onClick={onNewPolicy}><Plus className="w-4 h-4 mr-1" /> New Policy</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input placeholder="Search policies…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Draft">Draft</SelectItem>
            <SelectItem value="Under Review">Under Review</SelectItem>
            <SelectItem value="Approved">Approved</SelectItem>
            <SelectItem value="Published">Published</SelectItem>
            <SelectItem value="Superseded">Superseded</SelectItem>
            <SelectItem value="Withdrawn">Withdrawn</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[150px] h-9"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-[150px] h-9"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Depts</SelectItem>
            {['All','Front of House','Back of House','Kitchen','Management'].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[130px] h-9"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {['All Staff','Trainee','Staff','Supervisor','Manager','Admin'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        {uniqueOwners.length > 0 && (
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Owners</SelectItem>
              {uniqueOwners.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setStatusFilter('all'); setCategoryFilter('all'); setDeptFilter('all'); setRoleFilter('all'); setOwnerFilter('all'); }}>
            Clear ({activeFilterCount})
          </Button>
        )}
      </div>

      {/* Results count */}
      <p className="text-xs text-muted-foreground mb-2">{filtered.length} polic{filtered.length === 1 ? 'y' : 'ies'}</p>

      {/* Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground cursor-pointer select-none" onClick={() => toggleSort('name')}>
                  <span className="flex items-center gap-1">Policy <SortIcon field="name" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell cursor-pointer select-none" onClick={() => toggleSort('version')}>
                  <span className="flex items-center gap-1">Version <SortIcon field="version" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell cursor-pointer select-none" onClick={() => toggleSort('status')}>
                  <span className="flex items-center gap-1">Status <SortIcon field="status" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden lg:table-cell">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden lg:table-cell">Owner</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden lg:table-cell cursor-pointer select-none" onClick={() => toggleSort('effectiveDate')}>
                  <span className="flex items-center gap-1">Effective <SortIcon field="effectiveDate" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell cursor-pointer select-none" onClick={() => toggleSort('reviewDate')}>
                  <span className="flex items-center gap-1">Review Due <SortIcon field="reviewDate" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell cursor-pointer select-none" onClick={() => toggleSort('acknowledged')}>
                  <span className="flex items-center gap-1">Ack <SortIcon field="acknowledged" /></span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No policies found</p>
                  <Button size="sm" className="mt-3" onClick={onNewPolicy}>Create First Policy</Button>
                </td></tr>
              ) : filtered.map(p => (
                <tr key={p.id} className="hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => onViewPolicy(p.id)}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground">{p.policyName}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {p.policyNumber && <span className="text-xs text-muted-foreground">{p.policyNumber}</span>}
                      {p.isMandatory && <Badge variant="outline" className="text-[10px] px-1 py-0">Mandatory</Badge>}
                      {p.reviewOverdue && <Badge variant="destructive" className="text-[10px] px-1 py-0">Review Overdue</Badge>}
                      {p.overdueCount > 0 && <Badge variant="destructive" className="text-[10px] px-1 py-0">{p.overdueCount} overdue</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{p.version || '—'}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[p.lifecycleStatus || 'Draft'] || STATUS_COLORS.Draft}`}>
                      {p.lifecycleStatus || 'Draft'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell text-xs">{p.primaryCategoryName || '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell text-xs">{p.ownerName || '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell text-xs">
                    {p.effectiveDate ? format(parseISO(p.effectiveDate), 'd MMM yyyy') : '—'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell text-xs">
                    {p.reviewDate ? format(parseISO(p.reviewDate), 'd MMM yyyy') : '—'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell text-xs">
                    {p.requiresAcknowledgement ? `${p.acknowledgedCount}/${p.assignedCount}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="border border-border rounded-lg p-3 bg-card">
      <div className="flex items-center gap-2 text-muted-foreground mb-1">{icon}<span className="text-xs">{label}</span></div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
    </div>
  );
}
